import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { playerImageUrl } from '../config/playerImages'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters, AdvancedNote } from '../components/ToolTour'
import PickEmCard from './PickEmCard'
import { LEG_OPTIONS } from './pickEmConfig'

const C = {
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
  red: '#f87171',
}

const FIVE = LEG_OPTIONS.find(o => o.value === '5')
const THREE = LEG_OPTIONS.find(o => o.value === '3')

// Slide 1 visual: a 5 leg All In entry as it looks in Dabble
const SLIP = [
  { name: 'LeBron James', dir: 'More', line: '24.5 Points' },
  { name: 'Nikola Jokic', dir: 'More', line: '11.5 Rebounds' },
  { name: 'Stephen Curry', dir: 'Less', line: '4.5 Threes' },
  { name: 'Jayson Tatum', dir: 'More', line: '8.5 Rebounds' },
  { name: 'Luka Doncic', dir: 'More', line: '8.5 Assists' },
]

function EntrySlip({ isMobile }) {
  return (
    <div className="tt-rise" style={{
      width: '100%', maxWidth: isMobile ? 'none' : '400px', boxSizing: 'border-box', borderRadius: '16px', overflow: 'hidden',
      background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
      boxShadow: '0 16px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: isMobile ? '8px 12px' : '10px 14px', borderBottom: `1px solid ${C.border}` }}>
        <img src={BOOKMAKER_LOGOS.Dabble} alt="" style={{ width: 20, height: 20, borderRadius: '6px', objectFit: 'cover' }} />
        <span style={{ flex: 1, fontSize: isMobile ? '12.5px' : '13.5px', fontWeight: 800, color: C.text }}>Pick'em <span style={{ color: C.dim, fontWeight: 600 }}>· All In</span></span>
        <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted }}>5 legs</span>
        <span style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 800, color: C.green, padding: '2px 8px', borderRadius: '8px', backgroundColor: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>{FIVE.payout}x</span>
      </div>
      {SLIP.map((p, i) => (
        <div key={p.name} className="tt-rise" style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: isMobile ? '3px 12px' : '6px 14px',
          borderBottom: i < SLIP.length - 1 ? `1px solid ${C.border}` : 'none', animationDelay: `${0.15 + i * 0.08}s`,
        }}>
          <span style={{ width: isMobile ? 22 : 26, height: isMobile ? 22 : 26, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: 'rgba(148,163,184,0.12)', border: `1px solid ${C.borderStrong}` }}>
            <img src={playerImageUrl(p.name, 'NBA')} alt="" onError={e => { e.target.style.opacity = 0 }} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </span>
          <span style={{ flex: 1, minWidth: 0, fontSize: isMobile ? '12px' : '13px', fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
          <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600, color: C.muted, whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 800, color: p.dir === 'More' ? C.green : C.red }}>{p.dir}</span> {p.line}
          </span>
        </div>
      ))}
    </div>
  )
}

// Slide 2 visual: the payout turns into a price per leg, and picks either
// beat it or don't
function PriceToBeat({ isMobile }) {
  const tile = {
    flex: 1, minWidth: 0, padding: isMobile ? '10px 12px' : '12px 16px', borderRadius: '14px', boxSizing: 'border-box',
    background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
  }
  const label = { fontSize: isMobile ? '9.5px' : '10px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }
  const big = { fontSize: isMobile ? '22px' : '26px', fontWeight: 800, lineHeight: 1.1, marginTop: '4px', fontVariantNumeric: 'tabular-nums' }
  const picks = [
    { fair: 1.75, good: true, note: 'Below the price, +EV' },
    { fair: 2.05, good: false, note: 'Above it, like most picks' },
  ]
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '440px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
        <div className="tt-rise" style={tile}>
          <div style={label}>5 leg payout</div>
          <div style={{ ...big, color: C.text }}>{FIVE.payout}x</div>
        </div>
        <svg className="tt-fade" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.dim} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, animationDelay: '0.3s' }}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        <div className="tt-rise" style={{ ...tile, border: '1px solid rgba(96,165,250,0.45)', boxShadow: '0 0 24px rgba(59,130,246,0.2)', animationDelay: '0.4s' }}>
          <div style={label}>Price per leg</div>
          <div style={{ ...big, color: '#60a5fa' }}>{FIVE.threshold.toFixed(3)}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gap: isMobile ? '6px' : '8px', marginTop: isMobile ? '10px' : '14px' }}>
        {picks.map((p, i) => (
          <div key={p.fair} className="tt-rise" style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: isMobile ? '8px 12px' : '9px 14px', borderRadius: '10px',
            backgroundColor: p.good ? 'rgba(16,185,129,0.10)' : 'rgba(248,113,113,0.06)',
            border: `1px solid ${p.good ? 'rgba(16,185,129,0.35)' : 'rgba(248,113,113,0.25)'}`,
            animationDelay: `${0.8 + i * 0.15}s`,
          }}>
            <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 600, color: C.muted }}>Fair odds</span>
            <span style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 800, color: p.good ? C.green : C.red, fontVariantNumeric: 'tabular-nums' }}>{p.fair.toFixed(2)}</span>
            <span style={{ flex: 1, textAlign: 'right', fontSize: isMobile ? '11.5px' : '12.5px', fontWeight: 700, color: p.good ? C.green : C.red }}>{p.note}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Slide 3. Leg count sits top right, so its (i) opens downward and leftward.
function LegCountInfo() {
  return (
    <InfoTip label="Choosing your leg count" title="Choosing your leg count" align="end" placement="bottom">
      <strong style={{ color: C.text }}>5 legs</strong> has the easiest price to beat ({FIVE.threshold.toFixed(3)}) and the best long-term edge, with big swings.
      <span style={{ display: 'block', marginTop: '8px' }}><strong style={{ color: C.text }}>3 legs</strong> ({THREE.threshold.toFixed(3)}) is steadier, if you'd rather have smoother results.</span>
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Leg count', value: '5 legs', info: <LegCountInfo /> },
  { label: 'EV %', value: '3% to 20%' },
  { label: 'Min bookmakers', value: '6' },
  { label: 'Starts in', value: '0 to 12 hrs' },
  { label: 'Sort by', value: 'Highest EV' },
]

// Slide 4: the real card
function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'NBA', match: 'Los Angeles Lakers vs Golden State Warriors', market_type: 'player_points',
    selection: 'LeBron James Over 24.5', commence_time: commence,
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage
    <div style={{ width: '100%', maxWidth: isMobile ? '256px' : '340px' }}>
      <div style={{ zoom: isMobile ? 0.76 : 0.86 }}>
        <PickEmCard
          opp={opp}
          className="tt-card"
          ev={6.4}
          threshold={FIVE.threshold}
          legCount={FIVE.value}
          markers={{ market: 1, pick: 2, dabble: 3, threshold: 4, ev: 5, add: 6 }}
        />
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Pick', 'Dabble', 'Price to beat', 'EV', 'Add leg']} />
    </div>
  )
}

function pickemTourSlides(isMobile) {
  return [
    {
      title: 'How Pick\'em works',
      body: (
        <>
          Pick'em is Dabble's fixed payout multi. You pick More or Less on 2 to 12 player stat lines, and the payout is set by how many legs you take. A 5 leg entry pays {FIVE.payout}x.
          <AdvancedNote>Extremely high variance, with long losing runs between wins. New to EV betting? Start with the regular EV tool first.</AdvancedNote>
        </>
      ),
      visual: <EntrySlip isMobile={isMobile} />,
    },
    {
      title: 'How it beats Dabble',
      body: `Because the payout is fixed, every leg has a set price. ${FIVE.payout}x over 5 legs works out to ${FIVE.threshold.toFixed(3)} per leg. Over 99% of picks have fair odds above that, which is how Dabble wins. We find the few below it, so every leg is +EV and the edge multiplies across your entry.`,
      visual: <PriceToBeat isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'Here\'s our recommended setup. 5 legs gives the best edge, or pick 3 for steadier results. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using Pick\'em',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Tap the square on a card to add it, or open the Multi Builder and tap Auto Build.</li>
          <li>Open Dabble and enter the same picks as an All In entry.</li>
          <li>Place it, then tap Save to Tracker in the Multi Builder.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'New picks appear as they\'re found. Expect long losing runs, even with every leg +EV: a 5 leg entry loses far more often than it wins. Stick with it over hundreds of entries and the edge shows up in your profit.',
      visual: <ProfitSketch isMobile={isMobile} headline="Long-term play" sub="Judge it over hundreds of entries" />,
    },
  ]
}

export const openPickemTour = () => openTour('pickem')

export default function PickEmTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('pickem', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={pickemTourSlides(isMobile)}
      eyebrow="Pick'em EV"
      isMobile={isMobile}
      finalAction={hasAccess('pickem') ? { label: 'Get into it' } : { label: 'Unlock with Sports Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
